import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { IBreadcrumbItem } from 'dash-ui';

import { getCurrentTabFromSearchParam } from 'common/helpers/route.helper';
import { RoutesPath } from 'flow-components/routes/routes';

import {
  type IBreadcrumbScopeBuildParams,
  useOrganizationsBreadcrumbBuilder,
  usePropertiesBreadcrumbBuilder,
} from './breadcrumbs-scope-builders';
import { useSaleTeamBreadcrumbBuilder } from './breadcrumbs-scope-builders/use-sale-team-breadcrumb-builder';

export const useBreadcrumbBuilder = (): [
  IBreadcrumbItem[],
  (breadcrumbItems: IBreadcrumbItem[]) => void
] => {
  const [getAsyncPropertiesBreadcrumbItems] = usePropertiesBreadcrumbBuilder();
  const [getAsyncOrganizationsBreadcrumbItems] =
    useOrganizationsBreadcrumbBuilder();
  const [getAsyncSaleTeamBreadcrumbItems] = useSaleTeamBreadcrumbBuilder();

  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumbItem[]>([]);

  const { pathname, search } = useLocation();
  const { t } = useTranslation('common', { keyPrefix: 'labels' });
  const currentTab = getCurrentTabFromSearchParam(search);

  useEffect(() => {
    const fetchItems = async () => {
      const buildParams: IBreadcrumbScopeBuildParams = {
        t,
        pathname,
        currentTab,
      };

      let items: IBreadcrumbItem[] = [];

      if (pathname.includes(RoutesPath.Properties)) {
        items = await getAsyncPropertiesBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.Organization)) {
        items = await getAsyncOrganizationsBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.ExternalSales)) {
        items = await getAsyncSaleTeamBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.Dashboard)) {
        items = [
          {
            label: t('dashboard'),
            icon: 'chart',
            path: RoutesPath.Dashboard,
          },
        ];
      }

      setBreadcrumbItems(items);
    };

    fetchItems().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, t, currentTab]);

  return [breadcrumbItems, setBreadcrumbItems];
};
